import React, {ReactElement, useState} from "react";
import classNames from "classnames";
import {SubscriptionTier} from "@devour/client";
import {useGetSubscriptionTiers} from "@/hooks/useGetSubscriptionTiers";
import {resolveUserAvatar, DEFAULT_AVATAR, UserAvatarFields} from "@/utils/resolveUserAvatar";
import "./UserAvatar.scss";

/**
 * Fixed steps, plus `fill` for sites that already size their own wrapper.
 * New usage should pick a step so sizes cannot drift apart again.
 */
export type UserAvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "fill";

/**
 * The user shape an avatar can be built from. Deliberately loose — leaderboard
 * rows, chat members and full User objects all carry a subset of these, and the
 * component must render for every one of them.
 */
export interface UserAvatarUser extends UserAvatarFields {
    subscriptionTier?: string | SubscriptionTier | null;
    subscriptionTierStatus?: string | null;
}

interface Props {
    user: UserAvatarUser | null | undefined;
    size?: UserAvatarSize;
    /** Overrides the default placeholder. Only pass this for a genuinely different empty state. */
    fallback?: string;
    /**
     * Skip the gravatar step so a user with no upload reads as the surface's own
     * placeholder rather than an email-MD5 identicon. Brand-themed surfaces opt in.
     */
    skipGravatar?: boolean;
    /**
     * Subscription tier ring. On by default, and renders only when the user's
     * payload actually carries a tier — most leaderboard rows do not yet.
     */
    showTierRing?: boolean;
    /**
     * Escape hatch for a caller that already has the tiers loaded. Normally leave
     * this unset — the component fetches them itself.
     */
    tiers?: Array<SubscriptionTier>;
    /**
     * Appended as `?v=` to bust the browser cache after the user changes their
     * picture — the uploaded file keeps the same URL, so without this the old
     * image persists. Only needed on surfaces that can edit the avatar.
     */
    cacheKey?: string | number;
    className?: string;
    alt?: string;
    onClick?: () => void;
}

/**
 * Resolve the tier ring's level: 1 Scout / 2 Operator / 3 Legend, or null.
 *
 * `subscriptionTier` is a non-populated Ref in most payloads, so it arrives as an
 * id string and needs the tiers list to become a level. A populated object is
 * handled too, since some endpoints do populate it.
 */
function resolveTier(user: UserAvatarUser, tiers?: Array<SubscriptionTier>): { level: number; name?: string; color?: string } | null {
    const tier = user?.subscriptionTier;
    if (!tier) {
        return null;
    }
    if (typeof tier === "object") {
        return tier.tierLevel != null ? {level: tier.tierLevel, name: tier.name, color: tier.color} : null;
    }
    const found = tiers?.find(t => t.id === tier);
    return found?.tierLevel != null ? {level: found.tierLevel, name: found.name, color: found.color} : null;
}

/**
 * The one way to render a user's avatar — owns src, sizing, fallback,
 * broken-image handling and decorations, so each is fixed in one place.
 * Guilds are NOT users; a guild row renders its own icon instead.
 */
function UserAvatar(props: Props): ReactElement {
    const {
        user,
        size = "md",
        fallback = DEFAULT_AVATAR,
        skipGravatar = false,
        showTierRing = true,
        tiers,
        cacheKey,
        className,
        alt = "User avatar",
        onClick,
    } = props;

    /* Broken-image fallback, handled once here rather than per call site. */
    const [failed, setFailed] = useState(false);

    /*
     * Resolved here, not passed in. `subscriptionTier` arrives as a bare id
     * (autopopulate: false), so a ring needs the tier list to find its level —
     * requiring every call site to thread that prop is how the ring ended up
     * rendering nowhere. React Query dedupes to one request per page. No token is
     * read from redux: the endpoint is optionalAuthentication and getConfig pulls
     * from the store singleton, so this still renders outside a Provider (tests,
     * stories, embeds).
     */
    const {data: tiersRes} = useGetSubscriptionTiers(undefined, 100, 0, "");
    const resolvedTiers = tiers ?? tiersRes?.subscriptionTiers;

    const resolved = resolveUserAvatar(user, fallback, {skipGravatar});
    const base = failed || !resolved ? fallback : resolved;
    /*
     * Only bust when the user actually has an upload — the gravatar and the
     * shared placeholder never change, so a version param on them is noise.
     */
    const src = cacheKey != null && user?.profilePicture && !failed
        ? `${base}${base.includes("?") ? "&" : "?"}v=${cacheKey}`
        : base;

    const tier = showTierRing && user ? resolveTier(user, resolvedTiers) : null;
    const tierLevel = tier?.level ?? null;
    /* A paused subscription still holds its tier, so the ring shows but reads as muted. */
    const isPaused = String(user?.subscriptionTierStatus) === "PAUSED";
    /*
     * The ring is a colour and nothing else, so on its own it fails WCAG 1.4.1 and
     * is invisible to a screen reader. Name the tier in the accessible label instead
     * of inventing a second visual marker in a 24px circle.
     */
    const tierLabel = tier
        ? `${tier.name ?? `Tier ${tier.level}`}${isPaused ? " (payment paused)" : ""}`
        : null;

    const inner = (
        <div
            className={classNames(
                "user-avatar",
                `user-avatar--${size}`,
                tierLevel && `user-avatar--tier-${tierLevel}`,
                tierLevel && isPaused && "user-avatar--paused",
                onClick && "user-avatar--clickable",
                className,
            )}
            /* Admin-assigned tier color wins; the per-level metals are the fallback. */
            style={tier?.color ? { "--tier-ring": tier.color } as React.CSSProperties : undefined}
        >
            <img
                className="user-avatar__img"
                src={src}
                alt={alt && tierLabel ? `${alt} — ${tierLabel}` : alt}
                onError={() => setFailed(true)}
            />
        </div>
    );

    /*
     * A real <button> rather than a div with role/tabIndex — keyboard support and
     * focus come for free, and eslint-jsx-a11y rejects the hand-rolled version.
     */
    if (!onClick) {
        return inner;
    }
    return (
        <button
            type="button"
            className="user-avatar__button"
            onClick={onClick}
            aria-label={[alt, tierLabel].filter(Boolean).join(" — ") || undefined}
        >
            {inner}
        </button>
    );
}

export default UserAvatar;
